import type { Order, Shipment } from "../../types/DB2";
import { PersistentStorage } from "../storage/PersistentStorage";

export interface OfflineCacheSnapshot {
  version: number;
  shipments: Shipment[];
  orders: Order[];
  lastUpdatedAt: number;
}

const CACHE_VERSION = 1;
const CACHE_STORAGE_KEY = "offline:cache";

export class OfflineCacheManager {
  private cache: OfflineCacheSnapshot | null = null;

  constructor(
    private readonly storage: PersistentStorage,
    private readonly storageKey: string = CACHE_STORAGE_KEY
  ) {}

  async read(): Promise<OfflineCacheSnapshot> {
    if (this.cache) {
      return this.cache;
    }
    const stored = await this.storage.getItem<OfflineCacheSnapshot>(this.storageKey);
    this.cache = migrateCache(stored);
    return this.cache;
  }

  async write(partial: {
    shipments?: Shipment[];
    orders?: Order[];
    lastUpdatedAt?: number;
  }): Promise<OfflineCacheSnapshot> {
    const current = await this.read();
    const next: OfflineCacheSnapshot = {
      version: CACHE_VERSION,
      shipments: partial.shipments ?? current.shipments,
      orders: partial.orders ?? current.orders,
      lastUpdatedAt: partial.lastUpdatedAt ?? Date.now(),
    };
    await this.persist(next);
    return next;
  }

  async clear(): Promise<void> {
    const empty = createInitialCache();
    await this.persist(empty);
  }

  private async persist(snapshot: OfflineCacheSnapshot): Promise<void> {
    snapshot.version = CACHE_VERSION;
    this.cache = snapshot;
    await this.storage.setItem(this.storageKey, snapshot);
  }
}

const migrateCache = (
  snapshot: OfflineCacheSnapshot | null | undefined
): OfflineCacheSnapshot => {
  if (!snapshot) {
    return createInitialCache();
  }
  snapshot.version = CACHE_VERSION;
  if (!Array.isArray(snapshot.shipments)) {
    snapshot.shipments = [];
  }
  if (!Array.isArray(snapshot.orders)) {
    snapshot.orders = [];
  }
  if (!snapshot.lastUpdatedAt) {
    snapshot.lastUpdatedAt = Date.now();
  }
  return snapshot;
};

const createInitialCache = (): OfflineCacheSnapshot => ({
  version: CACHE_VERSION,
  shipments: [],
  orders: [],
  lastUpdatedAt: 0,
});

